/**
 * PILOT-3 (GDPR): Guest Consent Banner
 *
 * Shown as a full-screen gate before the guest can use the concierge.
 * GDPR Article 7 requirements:
 *  - Explicit opt-in (checkbox, not pre-ticked)
 *  - Clear description of what consent covers
 *  - Link to full privacy notice
 *  - Consent recorded in activity ledger with ISO timestamp
 *
 * Consent is stored in localStorage under 'zaxia_guest_consent_v1' with
 * { given: true, timestamp: ISO string, stayId: string }.
 * The backend receives a POST /api/activity-ledger/consent for the audit trail.
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { recordConsent } from '@/api';
import { PrivacyPage } from './PrivacyPage';

// localStorage key — version suffix allows future consent re-prompts if policy changes
export const CONSENT_STORAGE_KEY = 'zaxia_guest_consent_v1';

export interface GuestConsentRecord {
  given: boolean;
  timestamp: string;
  stayId: string;
}

/** Returns stored consent record, or null if not yet given */
export function getStoredConsent(): GuestConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestConsentRecord;
    return parsed.given ? parsed : null;
  } catch {
    return null;
  }
}

interface GuestConsentBannerProps {
  stayId: string;
  roomNumber: string;
  hotelName: string;
  onConsent: () => void;
}

export function GuestConsentBanner({
  stayId,
  roomNumber,
  hotelName,
  onConsent,
}: GuestConsentBannerProps) {
  const [checked, setChecked] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!checked) return;
    setIsSubmitting(true);

    const timestamp = new Date().toISOString();

    // Persist consent locally first — this is the source of truth for the UI gate.
    // Backend call is best-effort for the audit trail.
    const record: GuestConsentRecord = { given: true, timestamp, stayId };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));

    // Record in activity ledger (best-effort — don't block the user if this fails)
    try {
      await recordConsent(stayId, roomNumber);
    } catch (err) {
      // Non-fatal: localStorage consent is sufficient for the UI gate.
      // Log for debugging but don't surface to the user.
      console.warn('[CONSENT] Failed to record consent in activity ledger:', err);
    }

    setIsSubmitting(false);
    onConsent();
  }, [checked, stayId, roomNumber, onConsent]);

  return (
    <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Data consent required"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-cyan-500/8 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold tracking-wider mb-1">
            <span className="bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              ZAXIA
            </span>
          </h1>
          <p className="text-gray-400 text-xs tracking-widest uppercase">{hotelName}</p>
        </div>

        <h2 className="text-white text-lg font-semibold text-center mb-2">
          Welcome to your digital concierge
        </h2>
        <p className="text-gray-400 text-sm text-center mb-6">
          Before we begin, we need your permission to process your request data.
        </p>

        {/* Consent description */}
        <div className="bg-white/5 rounded-xl p-4 mb-5 text-sm text-gray-300 leading-relaxed">
          <p>
            To fulfil your requests (housekeeping, room service, bookings), we will store
            your name, room number, and request details for up to{' '}
            <strong className="text-white">7 days</strong>. Your data is never sold or
            shared with third parties.
          </p>
          <button
            onClick={() => setShowPrivacy(true)}
            className="mt-2 text-cyan-400 hover:text-cyan-300 underline text-xs"
          >
            Read our full Privacy Notice →
          </button>
        </div>

        {/* Consent checkbox — not pre-ticked (GDPR Article 7) */}
        <label className="flex items-start gap-3 cursor-pointer mb-5 group">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/10 text-teal-400 cursor-pointer flex-shrink-0"
            aria-label="I agree to the processing of my data"
          />
          <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
            I agree that {hotelName} may process my personal data (name, room number, service
            requests) to fulfil my concierge requests during my stay.
          </span>
        </label>

        {/* Accept button — disabled until checkbox ticked */}
        <button
          onClick={handleAccept}
          disabled={!checked || isSubmitting}
          className={`w-full py-3 rounded-xl text-sm font-semibold transition-all ${
            checked && !isSubmitting
              ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:from-cyan-400 hover:to-teal-400 shadow-lg shadow-teal-500/20'
              : 'bg-white/10 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? 'Saving…' : 'Accept & Continue'}
        </button>

        <p className="text-center text-xs text-gray-600 mt-4">
          You may withdraw consent at any time at hotel reception.
        </p>
      </div>
    </motion.div>
    <AnimatePresence>
      {showPrivacy && <PrivacyPage key="privacy" onClose={() => setShowPrivacy(false)} />}
    </AnimatePresence>
    </>
  );
}
